import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import KundaliChart from "../components/KundaliChart";
import {
  Sparkle, Calculator, ArrowLeft, Heart, Warning, Hand, Crown, Star, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import { useSavedReportLoader } from "../lib/useSavedReport";

export default function NavamsaPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSavedReportLoader({ expectedTool: "navamsa", setForm, setResult });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/navamsa/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='nv-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your D9 chart. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="navamsa-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="nv-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Heart size={14} weight="duotone" /> Free preview · D9 Navamsa Marital Karma Reader
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          D9 Navamsa · <span className="italic text-gold">Marital Karma</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          The Navamsa (D9) is the most important divisional chart in Vedic astrology for <b className="text-[#3F2D1B]">marriage karma</b>. Each rashi is sub-divided into 9 segments of 3°20' — every planet acquires a *deeper* sign that reveals its soul-level potency. Marriage is read primarily from D9, not D1. This tool renders both charts and decodes your spouse profile, 7th-house planets, Venus/Jupiter D9 placements, Vargottama anchors, plus precautions & a happy-marriage playbook.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nv-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nv-time" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <input type="text" required value={form.place} onChange={handle("place")} placeholder="Mumbai, India" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nv-place" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nv-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="nv-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Casting your Navamsa..." : "Reveal my D9 marital karma"}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-10 space-y-6" data-testid="nv-result">
            {/* Hero */}
            <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40">
              <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
                <Heart size={14} weight="fill" /> Your D9 Marital Karma Snapshot
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
                D9 Lagna · <span className="italic text-amber-300">{result.d9.lagna.rashi}</span>
              </h2>
              <div className="mt-3 text-amber-100/90 text-sm sm:text-base leading-relaxed">
                7th house of D9 sits in <b className="text-amber-300">{result.analysis.seventh_rashi}</b>, ruled by <b className="text-amber-300">{result.analysis.seventh_lord}</b>.
                {result.analysis.vargottama_planets?.length > 0 && (
                  <> You have <b className="text-amber-300">{result.analysis.vargottama_planets.length}</b> Vargottama planet(s) — destiny anchors.</>
                )}
              </div>
            </div>

            {/* Charts side-by-side */}
            <div className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <Sparkle size={14} weight="duotone" /> Your D1 (Rasi) vs D9 (Navamsa)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
                <div className="text-center" data-testid="nv-chart-d1">
                  <div className="label-eyebrow !text-[10px] text-[#3F2D1B]/70 mb-2">D1 · Rasi chart</div>
                  <div className="inline-block">
                    <KundaliChart kundali={result.d1} size={280} />
                  </div>
                  <div className="text-xs mt-2 text-muted">Lagna: <b className="text-[#3F2D1B]">{result.d1.lagna.rashi}</b></div>
                </div>
                <div className="text-center" data-testid="nv-chart-d9">
                  <div className="label-eyebrow !text-[10px] text-gold mb-2">D9 · Navamsa chart</div>
                  <div className="inline-block">
                    <KundaliChart kundali={result.d9} size={280} />
                  </div>
                  <div className="text-xs mt-2 text-muted">Lagna: <b className="text-[#3F2D1B]">{result.d9.lagna.rashi}</b></div>
                </div>
              </div>

              {/* D9 placement table */}
              <div className="mt-6 pt-4 border-t border-soft">
                <div className="label-eyebrow !text-[10px] text-gold mb-2">Planet placement: D1 → D9</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  {Object.entries(result.d9.planets).map(([p, info]) => {
                    const isVargottama = info.d1_rashi === info.rashi;
                    return (
                      <div
                        key={p}
                        className={`rounded-lg px-2.5 py-1.5 border ${isVargottama ? "bg-amber-100 border-amber-400" : "bg-white/60 border-soft"}`}
                        data-testid={`nv-placement-${p.toLowerCase()}`}
                      >
                        <div className="font-medium text-[#3F2D1B]">{p}{info.retrograde ? " (R)" : ""}{isVargottama ? " ★" : ""}</div>
                        <div className="text-[10px] text-muted leading-tight">{info.d1_rashi} → <b className={isVargottama ? "text-amber-700" : "text-[#3F2D1B]/80"}>{info.rashi}</b></div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-muted mt-2 italic">★ = Vargottama (same sign in D1 and D9 — strongest classical placement).</div>
              </div>
            </div>

            {/* Free insights: spouse personality + 7th lord tone */}
            <div className="space-y-4">
              {result.analysis.insights.filter(i => i.free).map((ins) => (
                <InsightCard key={ins.key} insight={ins} />
              ))}
            </div>

            {/* Premium: rest of insights + precautions + happy practices */}
            <PaywallGate
              tool="navamsa"
              toolLabel="D9 Navamsa · Marital Karma"
              birthDetails={form}
              report={result}
            >
              <div className="space-y-6">
                <div className="space-y-4">
                  {result.analysis.insights.filter(i => !i.free).map((ins) => (
                    <InsightCard key={ins.key} insight={ins} />
                  ))}
                </div>

                {/* Precautions */}
                <div className="card-surface rounded-2xl p-5 sm:p-7 bg-rose-50/60 border border-rose-200" data-testid="nv-precautions">
                  <div className="label-eyebrow text-rose-800 flex items-center gap-1.5 mb-3">
                    <Warning size={14} weight="duotone" /> Precautions for your marriage
                  </div>
                  <ul className="space-y-2.5">
                    {result.analysis.precautions.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-rose-950">
                        <Warning size={14} className="text-rose-600 flex-shrink-0 mt-0.5" weight="fill" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Happy practices */}
                <div className="card-surface rounded-2xl p-5 sm:p-7 bg-emerald-50/60 border border-emerald-200" data-testid="nv-happy">
                  <div className="label-eyebrow text-emerald-800 flex items-center gap-1.5 mb-3">
                    <Hand size={14} weight="duotone" /> Live a happy married life · daily practices
                  </div>
                  <ul className="space-y-2.5">
                    {result.analysis.happy_practices.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-emerald-950">
                        <CheckCircle size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </PaywallGate>

            {/* CTA — D9 Marriage specialist */}
            <ToolAstrologerCta
              requiredTechnique="Navamsa (D9) Marriage Reading"
              pitch={`Get a live D9 read — 2nd marriage, divorce risk, spouse-meeting dasha & remedies for your ${result.d9.lagna.rashi} Navamsa`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ---------- Sub-components ----------

function InsightCard({ insight }) {
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-6" data-testid={`nv-insight-${insight.key}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="font-serif-display text-lg sm:text-xl text-[#3F2D1B]">{insight.title}</h3>
        {insight.meta && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
            {insight.meta}
          </span>
        )}
      </div>
      <div className="mt-3 text-sm sm:text-base text-[#3F2D1B] leading-relaxed space-y-2">
        {renderRichBody(insight.body)}
      </div>
      {insight.precautions?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-soft space-y-1.5">
          {insight.precautions.map((p, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-[#3F2D1B]/85">
              <Warning size={12} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <span>{p}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderRichBody(text) {
  if (!text) return null;
  // Split paragraphs on double newlines; bold inline on **...**
  return text.split(/\n{2,}/).map((para, pi) => (
    <p key={pi}>{renderBold(para)}</p>
  ));
}

function renderBold(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <b key={i} className="text-[#3F2D1B]">{p.slice(2, -2)}</b>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
